import * as React from 'react';
import {
  SelectedAttribute,
  SegmentAttributeBase,
  SegmentFilterOperator,
  SegmentAttributeGroup,
  SegmentAttributeOperator,
  SegmentCompositionSelection
} from '../../../models/segments';
import _ from 'lodash';

type State = SegmentAttributeGroup;

/**
 * In all actions, position indicates the location of the attribute group
 * within the segment definition tree by traversing the predicates value.
 * For example a position of `[2,1]` would locate the group at `state.predicates[2].predicates[1]`
 * The root level group is indicated with a position of `[]`
 *
 * example:
 * ```
 * state: {
 *   operator: 1,
 *   predicates: [
 *     {
 *        attributeId: 1000
 *        ...
 *     },
 *     {
 *       operator: 0,
 *       predicates: [
 *         {
 *           attributeId: 1001
 *           ...
 *         }
 *       ]
 *     }
 *   ]
 * }
 * ```
 * the positon of the attribute group containing {attributeId: 1000} would be [] (i.e. state.predicates)
 * the positon of the attribute group containing {attributeId: 1001} would be [1] (i.e. state.predicates[1])
 */
type SetAttributesAction = { type: 'setAttributes'; attributes: State };
type ApplyAttributesAction = {
  type: 'applyAttributes';
  checked: number[];
  attributes: SegmentAttributeBase[];
  position: number[];
};
type DeleteAttributeAction = {
  type: 'deleteAttribute';
  attributeId: number;
  position: number[];
  attributeIndex: number;
};
type UpdateAttributeAction = {
  type: 'updateAttribute';
  attribute: SelectedAttribute;
  position: number[];
  attributeIndex: number;
};
type UpdateOperatorAction = { type: 'updateOperator'; operator: SegmentAttributeOperator; position: number[] };
type AddGroupAction = {
  type: 'addGroup';
  attributeIdsToAdd: number[];
  afterAttributeId: number;
  afterIsTransactional?: boolean;
  attributes: SegmentAttributeBase[];
  position: number[];
};
type ConvertAttributeToGroupAction = {
  type: 'convertAttributeToGroup';
  attribute: SelectedAttribute;
  newGroupAttributeIds: number[];
  attributes: SegmentAttributeBase[];
};
type UpdateTransactionalFilter = {
  type: 'updateTransactionalFilter';
  attribute: SelectedAttribute;
  position: number[];
  attributeIndex: number;
};

type AddNewBaseGroup = {
  type: 'addNewBaseGroup';
  newGroupAttributeIds: number[];
  attributes: SegmentAttributeBase[];
};

type Action =
  | SetAttributesAction
  | ApplyAttributesAction
  | DeleteAttributeAction
  | UpdateAttributeAction
  | UpdateOperatorAction
  | AddGroupAction
  | ConvertAttributeToGroupAction
  | UpdateTransactionalFilter
  | AddNewBaseGroup;
type Dispatch = (action: Action) => void;

interface SegmentCompositionValue {
  selectedComoposition: SegmentCompositionSelection;
  changeSelectedComposition: React.Dispatch<React.SetStateAction<SegmentCompositionSelection>>;
}

/**
 * Context for sharing segment attribute configuration and actions throughout build/edit segment components
 */
export const BuildSegmentContext = React.createContext<{ selectedAttributes: State; dispatch: Dispatch } | undefined>(
  undefined
);

/**
 * Context for sharing excluded segment attribute configuration and actions throughout build/edit segment components
 */
export const BuildSegmentExcludedContext = React.createContext<
  { selectedAttributes: State; dispatch: Dispatch } | undefined
>(undefined);

export const BuildIncludeSelectedCompositionContext = React.createContext<SegmentCompositionValue | undefined>(
  undefined
);

export const BuildExcludeSelectedCompositionContext = React.createContext<SegmentCompositionValue | undefined>(
  undefined
);

/**
 * Handles applyAttributes action
 */
const handleApplyAttributesAction = (state: State, action: ApplyAttributesAction) => {
  const nextState = _.cloneDeep(state);

  // Zero attributes are checked this will remove that attribute grouping
  if (action.checked.length === 0) {
    mutateNode(
      nextState,
      (parentNode) => {
        const lastPosition = action.position[action.position.length - 1];
        parentNode.predicates.splice(lastPosition, 1);
      },
      action.position.slice(0, -1)
    );
  } else {
    mutateNode(nextState, (node) => applyAttributes(node, action.checked, action.attributes), action.position);
  }
  return nextState;
};

/**
 * Handles deleteAttribute action
 */
const handleDeleteAttributesAction = (state: State, action: DeleteAttributeAction) => {
  const nextState = _.cloneDeep(state);
  mutateNode(
    nextState,
    (node) => {
      node.predicates.splice(action.attributeIndex, 1);
      // if there are no items left in the group, remove the group
      if (node.predicates.length == 0) {
        mutateNode(
          nextState,
          (parentNode) => {
            const lastPosition = action.position[action.position.length - 1];
            parentNode.predicates.splice(lastPosition, 1);
          },
          action.position.slice(0, -1)
        );
        // if there is only one predicate left in a group and it is a group, remove the surrounding group (splice the child item into the parent group)
      } else if (node.predicates.length > 0 && 'predicates' in node.predicates[0]) {
        const remainder = node.predicates;
        mutateNode(
          nextState,
          (parentNode) => {
            const lastPosition = action.position[action.position.length - 1];
            parentNode.predicates.splice(lastPosition, 1, remainder[0]);
          },
          action.position.slice(0, -1)
        );
      }
    },
    action.position
  );

  return nextState;
};

/**
 * Handles updateAttribute action
 */
const handleUpdateAttributeAction = (state: State, action: UpdateAttributeAction) => {
  const nextState = _.cloneDeep(state);
  mutateNode(
    nextState,
    (node) => {
      node.predicates = node.predicates.map((existingAttribute, index) => {
        if ('attributeId' in existingAttribute && index == action.attributeIndex) {
          return action.attribute;
        }
        return existingAttribute;
      });
    },
    action.position
  );
  return nextState;
};

/**
 * Handles updateOperator action
 */
const handleUpdateOperatorAction = (state: State, action: UpdateOperatorAction) => {
  const nextState = _.cloneDeep(state);
  mutateNode(
    nextState,
    (node) => {
      node.operator = action.operator;
    },
    action.position
  );
  return nextState;
};

/**
 * Handles convertAttributeToGroup action
 */
const handleConvertAttributeToGroupAction = (state: State, action: ConvertAttributeToGroupAction) => {
  // convert an attribute to a group and apply newly selected attributes to the new group
  const newGroup = {
    operator: 0,
    predicates: [action.attribute]
  };
  applyAttributes(newGroup, action.newGroupAttributeIds, action.attributes);
  // swap out the original attribute for the new group
  return {
    operator: state.operator,
    predicates: state.predicates.map((predicate) => {
      if (
        'attributeId' in predicate &&
        predicate.attributeId === action.attribute.attributeId &&
        predicate.isTransactional === action.attribute.isTransactional
      ) {
        return newGroup;
      }
      return predicate;
    })
  };
};

const handleApplyNewBaseGroup = (state: State, action: AddNewBaseGroup) => {
  const newGroup = {
    operator: 0,
    predicates: []
  };
  applyAttributes(newGroup, action.newGroupAttributeIds, action.attributes);

  return {
    operator: state.operator,
    predicates: state.predicates.concat(newGroup)
  };
};

/**
 * Handles addGroup action
 */
const handleAddGroupAction = (state: State, action: AddGroupAction) => {
  if (action.attributeIdsToAdd.length === 0) {
    return state;
  }
  const nextState = _.cloneDeep(state);
  const newGroup = {
    operator: SegmentAttributeOperator.And,
    predicates: []
  };
  applyAttributes(newGroup, action.attributeIdsToAdd, action.attributes);
  // if adding a group to an attribute in the base level, convert the attribute to a group first
  if (action.position.length === 0) {
    const attributeIndex = state.predicates.findIndex(
      (predicate) =>
        'attributeId' in predicate &&
        predicate.attributeId === action.afterAttributeId &&
        predicate.isTransactional === action.afterIsTransactional
    );
    nextState.predicates.splice(attributeIndex, 1, {
      operator: SegmentAttributeOperator.And,
      predicates: [state.predicates[attributeIndex], newGroup]
    });
  } else {
    // otherwise simply add the new group after the appropriate attribute
    mutateNode(
      nextState,
      (node) => {
        const location = node.predicates.findIndex(
          (predicate) =>
            'attributeId' in predicate &&
            predicate.attributeId === action.afterAttributeId &&
            predicate.isTransactional === action.afterIsTransactional
        );
        node.predicates.splice(location + 1, 0, newGroup);
      },
      action.position
    );
  }
  return nextState;
};

const handleUpdateTransactionalFilter = (state: State, action: UpdateTransactionalFilter) => {
  const nextState = _.cloneDeep(state);
  mutateNode(
    nextState,
    (node) => {
      node.predicates = node.predicates.map((existingAttribute, index) => {
        if ('attributeId' in existingAttribute && index == action.attributeIndex) {
          return action.attribute;
        }
        return existingAttribute;
      });
    },
    action.position
  );
  return nextState;
};

/**
 * Reducer for the BuildSegmentContext Provider
 * Defines actions on a segment's selected attributes while building or editing a segment
 */
export function buildSegmentReducer(state: State, action: Action) {
  switch (action.type) {
    case 'setAttributes': {
      return action.attributes;
    }
    case 'applyAttributes': {
      return handleApplyAttributesAction(state, action);
    }
    case 'deleteAttribute': {
      return handleDeleteAttributesAction(state, action);
    }
    case 'updateAttribute': {
      return handleUpdateAttributeAction(state, action);
    }
    case 'updateOperator': {
      return handleUpdateOperatorAction(state, action);
    }
    case 'convertAttributeToGroup': {
      return handleConvertAttributeToGroupAction(state, action);
    }
    case 'addGroup': {
      return handleAddGroupAction(state, action);
    }
    case 'updateTransactionalFilter': {
      return handleUpdateTransactionalFilter(state, action);
    }
    case 'addNewBaseGroup': {
      return handleApplyNewBaseGroup(state, action);
    }
    default: {
      // @ts-ignore - default will only be reached if a new action type is unaccounted for
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * Mutate a single attribute group node on the segment tree
 * @param groupNode the attribute group node object to mutate
 * @param mutate a function called to perform the mutation
 * @param position the position of the node as an array of predicate array locations starting with the root predicates property.
 *   ex. to mutate the root object, postion = []. To mutate the second child of the root predicates property, position = [1]
 * @returns null
 */
function mutateNode(
  groupNode: SegmentAttributeGroup,
  mutate: (currentGroup: SegmentAttributeGroup) => void,
  position: number[]
) {
  if (position.length === 0) {
    mutate(groupNode);
    return;
  }
  if ('predicates' in groupNode.predicates[position[0]]) {
    mutateNode(groupNode.predicates[position[0]] as SegmentAttributeGroup, mutate, position.slice(1));
  }
}

/**
 * Resolve checked attribute ids from attribute select dialog with previous predicates property of a SegmentAttributeGroup
 * @param node the segment tree node to mutate
 * @param checked an array of attribute ids requested by the user
 * @param attributes an array of attribute detail objects containing necessary attribute metadata
 */
function applyAttributes(node: SegmentAttributeGroup, checked: number[], attributes: SegmentAttributeBase[]) {
  // for newly checked attributes at the base level, add and create default attribute filter objects based on filter type
  //@ts-ignore
  const newAttributes: SelectedAttribute[] = checked.map((attributeId) => {
    const attribute = attributes.find((attr) => attr.id === attributeId);
    const commonAttributeInfo = {
      attributeId: attribute.id,
      columnName: attribute.columnName,
      name: attribute.name
    };
    if (attribute.filterType === 'options') {
      return {
        ...commonAttributeInfo,
        filterOperator: SegmentFilterOperator.Is,
        options: []
      };
    }
    if (attribute.filterType === 'range') {
      return {
        ...commonAttributeInfo,
        filterOperator: SegmentFilterOperator.Is,
        range: { min: null, max: null }
      };
    }
    if (attribute.filterType === 'datetime') {
      return {
        ...commonAttributeInfo,
        filterOperator: SegmentFilterOperator.Is,
        dateRange: { min: null, max: null }
      };
    }
    if (attribute.isTransactional) {
      return {
        ...commonAttributeInfo,
        filterOperator: SegmentFilterOperator.Where,
        where: 'all',
        isTransactional: true
      };
    }
  });
  node.predicates = [...node.predicates, ...newAttributes];
}

/**
 * Hook for components consuming the BuildSegmentContext or BuildSegmentExcludedContext
 */
export function useBuildSegment(exclude?: boolean) {
  const context = React.useContext(exclude ? BuildSegmentExcludedContext : BuildSegmentContext);

  if (context === undefined) {
    throw new Error('useBuildSegment must be used within a BuildSegmentProvider');
  }

  return context;
}

export function useBuildSegmentComposition(exclude?: boolean) {
  const context = React.useContext(
    exclude ? BuildExcludeSelectedCompositionContext : BuildIncludeSelectedCompositionContext
  );

  if (context === undefined) {
    throw new Error('useBuildSegmentComposition must be used within a BuildSegmentProvider');
  }

  return context;
}
